import React from 'react';
import { HStack, Text } from '@chakra-ui/react';
import { FiHelpCircle } from 'react-icons/fi';
import { Tooltip } from '../ui/Tooltip';
import { Box } from '@chakra-ui/react';

interface FeatureTextWithTooltipProps {
  text: string;
  fontSize?: string;
  color?: string;
  textDecoration?: string;
}

const TOOLTIP_DESCRIPTIONS = {
  aiConversations: 'Coach conversations are interactions with Coach where you ask questions, get health insights, or receive genetic analysis. Each conversation uses credits based on the length and complexity of the exchange.',
  conversationsMo: 'Coach conversations are interactions with Coach where you ask questions, get health insights, or receive genetic analysis. Each conversation uses credits based on the length and complexity of the exchange. The number shown is an approximate estimate based on typical conversation lengths.',
  profile: 'A profile represents one person\'s genetic and health data. You can create separate profiles for yourself, family members, or clients. Each profile has its own genetic data, health records, wellness goals, and notes.',
  sharedProfiles: 'Shared profiles allow you to give view-only access to your profile data to others (like family members, healthcare providers, or Goals Partners). They can view your information but cannot make changes.',
  unlimitedFreeCollaborators: 'Free collaborators can help you manage your health data at no cost to them. They can view and help manage shared records without needing a paid subscription. Only the profile owner needs a subscription.',
  addOns: 'Profile add-ons allow Premium users to add additional profiles beyond the included amount. Each additional profile costs $25 one-time (permanent). This is perfect for managing health data for multiple family members.',
  wellnessGoals: 'Wellness goals help you track health objectives like exercise, nutrition, sleep, and more. You can create unlimited wellness goals on all plans. Only goals with status "in-progress" are considered active. Goals in other statuses (paused, completed, cancelled) are not counted as active.',
  wellnessGoalsAnalytics: 'Wellness goals analytics provides detailed insights and visualizations for your wellness goals. Track your progress with trend charts, monitor engagement metrics, and analyze performance over time. Key features include streak tracking to see your consistency, compliance monitoring to understand adherence patterns, and performance metrics to measure your progress toward goals.',
  wgsVcf: 'WGS (Whole Genome Sequencing) and VCF (Variant Call Format) files contain genetic data from clinical sequencing. These files typically include 4-5 million variants, compared to consumer genetic tests (like 23andMe or AncestryDNA) which typically include 600,000-700,000 variants. Note: While WGS/VCF files contain more total variants, consumer tests are specifically designed to include well-studied, clinically relevant SNPs. Maximum file size: 2GB. Each profile can have a maximum of 1 VCF file or file 1GB or larger.',
  geneticFileUploads: 'Order a Livewello DNA kit for the best coverage, or upload genetic data files up to 2GB in size. Supported upload formats include VCF, 23andMe, AncestryDNA, and other common genetic data formats. Files larger than 2GB cannot be processed.',
  customGeneticReports: 'Custom genetic reports are personalized reports that you can create to analyze specific genetic variants, health conditions, or wellness topics.',
  limitedByStorage: 'The number of genetic files you can upload is limited by your storage allowance. Each genetic file can be up to 2GB in size. If you reach your storage limit, you\'ll need to either upgrade your plan for more storage or delete existing files to make room for new uploads. Additionally, each profile can have a maximum of 1 VCF file or file 1GB or larger, regardless of subscription plan.',
  comprehensiveGeneticReport: 'Comprehensive genetic reports provide detailed, evidence-based genetic insights across multiple health and wellness domains. Available sections include Health & Longevity, Nutrition & Metabolism, Medication & Drug Response, Fitness & Physical Performance, Mind & Mood, Wellness & Lifestyle, Detox & Environmental, and more. Each section analyzes your specific genetic variants and provides actionable insights based on your unique genetic profile.',
  mcpApiKeys: 'MCP (Model Context Protocol) lets you connect AI assistants like Claude or ChatGPT directly to your Livewello account. Your AI can then read your health records, wellness goals, documents, and genetic data — enabling richer, more personalized conversations.\n\nAll plans include 1 MCP API key.\n\nData access limits vary by plan:\n\nGenetic variant queries\n• Free: 50 variants/query, 500/day\n• Basic: 200 variants/query, 2,000/day\n• Premium: 500 variants/query, 10,000/day\n\nWrite operations (create/update/delete)\n• Free: 50/hour\n• Basic: 200/hour\n• Premium: 500/hour',
};

export const FeatureTextWithTooltip: React.FC<FeatureTextWithTooltipProps> = ({
  text,
  fontSize = 'sm',
  color,
  textDecoration,
}) => {
  // Detect which tooltip to show based on feature text
  const getTooltipInfo = (): { label: string; showTooltip: boolean } | null => {
    const lowerText = text.toLowerCase();
    
    // Check for MCP access
    if (lowerText.includes('mcp')) {
      return { label: TOOLTIP_DESCRIPTIONS.mcpApiKeys, showTooltip: true };
    }

    // Check for "comprehensive genetic report" (most specific, before custom)
    if (lowerText.includes('comprehensive genetic report')) {
      return { label: TOOLTIP_DESCRIPTIONS.comprehensiveGeneticReport, showTooltip: true };
    }
    
    // Check for "custom genetic reports" (most specific)
    if (lowerText.includes('custom genetic reports')) {
      return { label: TOOLTIP_DESCRIPTIONS.customGeneticReports, showTooltip: true };
    }
    
    // Check for "limited by storage" (most specific)
    if (lowerText.includes('limited by storage')) {
      return { label: TOOLTIP_DESCRIPTIONS.limitedByStorage, showTooltip: true };
    }
    
    // Check for "shared profiles" first (most specific)
    if (lowerText.includes('shared profiles')) {
      return { label: TOOLTIP_DESCRIPTIONS.sharedProfiles, showTooltip: true };
    }
    
    // Check for "unlimited free collaborators"
    if (lowerText.includes('unlimited free collaborators')) {
      return { label: TOOLTIP_DESCRIPTIONS.unlimitedFreeCollaborators, showTooltip: true };
    }
    
    // Check for "+ add-ons" or "add-ons" or "add ons"
    if (lowerText.includes('+ add-ons') || lowerText.includes('add-ons') || (lowerText.includes('add') && lowerText.includes('on'))) {
      return { label: TOOLTIP_DESCRIPTIONS.addOns, showTooltip: true };
    }
    
    // Check for "conversations/mo" or "conversations per month"
    if (lowerText.includes('conversations/mo') || lowerText.includes('conversations per month') || (lowerText.includes('conversations') && lowerText.includes('/mo'))) {
      return { label: TOOLTIP_DESCRIPTIONS.conversationsMo, showTooltip: true };
    }
    
    // Check for "AI conversations"
    if (lowerText.includes('coach conversations') || lowerText.includes('ai conversations')) {
      return { label: TOOLTIP_DESCRIPTIONS.aiConversations, showTooltip: true };
    }
    
    // Check for "WGS/VCF" or "WGS" or "VCF processing"
    if (lowerText.includes('wgs/vcf') || lowerText.includes('wgs') || (lowerText.includes('vcf') && lowerText.includes('processing'))) {
      return { label: TOOLTIP_DESCRIPTIONS.wgsVcf, showTooltip: true };
    }
    
    // Check for genetic file uploads (most specific patterns first)
    if (lowerText.includes('genetic file uploads') || lowerText.includes('genetic file upload')) {
      return { label: TOOLTIP_DESCRIPTIONS.geneticFileUploads, showTooltip: true };
    }
    
    // Check for "genetic data file" or "genetic data files"
    if (lowerText.includes('genetic data file') || lowerText.includes('genetic data files')) {
      return { label: TOOLTIP_DESCRIPTIONS.geneticFileUploads, showTooltip: true };
    }
    
    // Check for "genetic file" (when in context of uploads/limits, but not WGS/VCF which is already handled)
    if (lowerText.includes('genetic file') && !lowerText.includes('wgs') && !lowerText.includes('vcf')) {
      return { label: TOOLTIP_DESCRIPTIONS.geneticFileUploads, showTooltip: true };
    }
    
    // Check for "wellness goals analytics" first (most specific)
    if (lowerText.includes('wellness goals analytics')) {
      return { label: TOOLTIP_DESCRIPTIONS.wellnessGoalsAnalytics, showTooltip: true };
    }
    
    // Check for "active wellness goals" or "wellness goals"
    // Match patterns like "5 active wellness goals", "50 active wellness goals", "unlimited wellness goals", etc.
    if (lowerText.includes('wellness goals') || lowerText.includes('active wellness goals')) {
      return { label: TOOLTIP_DESCRIPTIONS.wellnessGoals, showTooltip: true };
    }
    
    // Check for "profile" or "profiles" but not "shared profiles" (already checked above)
    // Match patterns like "1 profile", "3 profiles", "10 profiles included", etc.
    const profilePattern = /\b(profile|profiles)\b/;
    if (profilePattern.test(lowerText) && !lowerText.includes('shared')) {
      return { label: TOOLTIP_DESCRIPTIONS.profile, showTooltip: true };
    }
    
    return null;
  };

  const tooltipInfo = getTooltipInfo();

  if (!tooltipInfo) {
    // No tooltip needed, just return the text
    return (
      <Text fontSize={fontSize} color={color} textDecoration={textDecoration}>
        {text}
      </Text>
    );
  }

  return (
    <HStack gap={1} alignItems="center" display="inline-flex" flexWrap="nowrap">
      <Text fontSize={fontSize} color={color} textDecoration={textDecoration} display="inline">
        {text}
      </Text>
      <Tooltip label={tooltipInfo.label} placement="top">
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          cursor="help"
          color={color || 'gray.500'}
          _hover={{ color: 'brand.500' }}
          ml={1}
          flexShrink={0}
        >
          <FiHelpCircle size={14} />
        </Box>
      </Tooltip>
    </HStack>
  );
};

